/* eslint-disable max-lines */
import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewEncapsulation,
  ViewChild,
} from '@angular/core';
import {
  BodyScrollEvent,
  ColDef,
  ColGroupDef,
  ColumnApi,
  GridApi,
  GridOptions,
  GridReadyEvent,
  IDatasource,
  NodeChildDetails,
  RowClickedEvent,
  RowDoubleClickedEvent,
  RowDragEvent,
  RowGroupOpenedEvent,
  RowNode,
  SelectionChangedEvent,
  CellClickedEvent,
} from 'ag-grid-community';
import {
  cloneDeep,
  compact,
  filter,
  find,
  findIndex,
  get,
  has,
  includes,
  isEmpty,
  isEqual,
  isFunction,
  isNumber,
  map,
  merge,
  omit,
  set,
  some,
  toLower,
  uniq,
  unset,
  forEach,
  isNil,
  remove,
  startCase,
  mergeWith,
  isArray,
  reject,
  difference,
} from 'lodash';
import { BehaviorSubject, Subject } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter as rxfilter,
} from 'rxjs/operators';
import {
  DEFAULT_GRID_HEIGHT,
  DEFAULT_MINIMUM_GRID_HEIGHT,
  DEFAULT_TREE_CHILDREN_PROPERTY,
  DEFAULT_TREE_COL_ID,
  FilterModelComposer,
  GetQuickFilterTextForTreeViewType,
  ListColumn,
  ROW_SELECTOR_ID,
  SortModelField,
  XuxGridApi,
} from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions';
import {
  COLUMN_SELECTOR_ID,
  GridView,
  SelectOption,
  ToolbarButtonDefinition,
  XuxGridAction,
  XuxGridOptions,
} from '@geniuspos/ui-shared';
import { ComponentStaticText } from '../form/input/input.component';
import {
  GRID_ROW_MODEL_TYPE,
  GridRowHeightName,
} from '@geniuspos/ui-shared/index';
import { FilterModel } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/grid-view.type';
import { commonGridFrameworkComponents } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/xux-grid';
import { XUX_GRID_ACTIONS } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/types/grid-action.type'; // eslint-disable-line max-len
import { shouldEmitAction } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/utils';
import {
  composeColumnDefinition,
  DEFAULT_GRID_OPTIONS,
  OPTIONS_COLUMN_DEFINITION,
  ROW_DRAG_COLUMN_DEFINITION,
  ROW_REMOVE_COLUMN_DEFINITION,
  ROW_SELECTION_COLUMN_DEFINITION,
} from '@geniuspos/ui-shared/modules/xdm-shared-components/components/xux-grid/system-column-definitions';
import { DEFAULT_TEXT_FIELD } from '../../../config';
import { CustomSelectAllComponent } from '@geniuspos/ui-shared/modules/xdm-shared-components/components/xux-grid/custom-select-all/custom-select-all.component';
import { DEFAULT_VALUE_FIELD } from '@geniuspos/ui-shared/modules/xdm-shared-components/definitions/constants/local';
import { XuxGridActionsPanelCustomComponent } from '../xux-grid-actions-panel-custom';
import {
  XVIGridAction,
  CUSTOM_XUX_GRID_ACTIONS,
  IXVIListColumn,
} from '@app/shared/types';
import { I18n } from '@app/i18n/strings-keys';
import { XVII18nService } from '@app/i18n/services';
import { Observable, observableOf } from '@app/shared/rx';
import { asObservable } from '@app/shared/utils';

const RECALCULATE_GRID_HEIGHT_DEBOUNCE_TIME = 1000;

// eslint-disable  @typescript-eslint/no-inferrable-types
// eslint-disable-next-line @angular-eslint/prefer-on-push-component-change-detection
@Component({
  selector: 'xux-grid-custom',
  templateUrl: './xux-grid-custom.component.html',
  styleUrls: ['./styles/xux-grid-custom.component.scss'],
  encapsulation: ViewEncapsulation.None,
})
export class XuxGridCustomComponent implements OnInit, OnChanges {
  @Input() public items: object[] = [];
  @Input() public columns: ListColumn[] = [];
  @Input() public view: GridView;
  @Input() public options: XuxGridOptions;
  @Input() public userGridOptions: GridOptions;
  @Input() public buttons: ToolbarButtonDefinition[];
  @Input() public rowButtons: ToolbarButtonDefinition[];
  @Input() public pageCount: number;
  @Input() public showLoader: boolean;
  @Input() public userComponents: { [key: string]: any };
  @Input() public pinnedTopRowData: any[];
  @Input() public disabled: boolean = false;
  @Input() public artificialInputCheckForSearchPanel: boolean = false;
  @Input() public set i18n(staticTextContent: ComponentStaticText) {
    this.staticTextContent = {
      ...this.staticTextContent,
      ...(staticTextContent || {}),
    };
  }

  @Output() public action: EventEmitter<XVIGridAction> =
    new EventEmitter<XVIGridAction>();

  @ViewChild(XuxGridActionsPanelCustomComponent, { static: false })
  public actionPanel: XuxGridActionsPanelCustomComponent;

  public staticTextContent: ComponentStaticText = {
    inlineAddButtonCaption: this.i18nService.translate(I18n.Common.AddNewItem),
    search: this.i18nService.translate(I18n.Common.Search),
  };
  public columnDefinitions: (ColDef | ColGroupDef)[] = [];
  public rowData: object[] = [];
  public context: XuxGridApi = {
    emitItemAction: this.emitAction.bind(this),
    buttons: new BehaviorSubject([]),
    onBodyScroll: new Subject(),
  };
  public gridOptions: GridOptions = {};
  public actionAcceptanceValidators: Function[] = [];
  public selectedItems: object[] = [];
  public frameworkComponents: { [key: string]: any };

  public showSearch: boolean = false;
  public searchPlaceholder: string = '';
  public inlineAddButtonCaption: string = this.staticTextContent
    .inlineAddButtonCaption as string;

  public gridHeight$: Observable<string | number> =
    observableOf(DEFAULT_GRID_HEIGHT);

  public gridMinHeight: string | number = DEFAULT_MINIMUM_GRID_HEIGHT;
  public allCollapsed: boolean;
  public skipColumnSelectionChangeEvent: boolean = false;
  public gridApi: GridApi;
  public columnApi: ColumnApi;
  public gridRowHeight: GridRowHeightName;
  public pinnedRowPresent: boolean = false;
  public isCellEditInProgress: boolean = false;
  public customOverlayNoRowsTemplate: string = '';

  private lastEmittedFilterModel: FilterModel = {};
  private lastEmittedSortModel: SortModelField[] = [];
  private quickFilterSearchString: string = null;

  private columnSelectorValues$: BehaviorSubject<SelectOption[]> =
    new BehaviorSubject([]);
  private calculatedBodyHeight$: BehaviorSubject<number> =
    new BehaviorSubject<number>(0);

  public constructor(
    private readonly ngZone: NgZone,
    private readonly i18nService: XVII18nService,
  ) {}

  @HostListener('MozMousePixelScroll', ['$event'])
  public onScroll(event: MouseEvent): void {
    const shouldPreventDefault =
      this.gridOptions.domLayout !== 'autoHeight' &&
      !this.gridOptions.suppressPreventDefaultOnMouseWheel;

    if (shouldPreventDefault) {
      event.preventDefault();
    }
  }

  public ngOnInit(): void {
    this.gridRowHeight = this.getInitialRowHeight();
    this.gridOptions = this.composeGridOptions();
    this.actionAcceptanceValidators = get(
      this,
      'options.actionAcceptanceValidators',
      [],
    ).filter(isFunction);

    this.allCollapsed = !get(this.options, 'defaultExpanded', false);

    this.frameworkComponents = Object.assign(
      {},
      commonGridFrameworkComponents,
      this.userComponents,
    );

    this.updateSearchField();
    this.calculatedBodyHeight$
      .pipe(
        rxfilter(() => this.gridApi && this.isRowsAutoheightMode()),
        distinctUntilChanged(),
        debounceTime(RECALCULATE_GRID_HEIGHT_DEBOUNCE_TIME),
      )
      .subscribe(() => this.gridApi.onRowHeightChanged());
  }

  public ngOnChanges(changes: SimpleChanges): void {
    this.updateSearchField();

    // prevent zero page count, pageCount should be always at least 1
    if ('pageCount' in changes && this.pageCount === 0) {
      this.pageCount = 1;
    }

    // && !isEqual(changes.columns.previousValue, this.columns)
    // These should be reverted once 2.6 release branches will be cut off
    // and there should be better way to track changes in columns
    // NOTE: This was removed because of "changes.columns.previousValue" and "this.columns"
    // are always equal and always "columnsChanged" flag equals to "false"
    const columnsChanged = 'columns' in changes;
    const optionsChanged =
      'options' in changes &&
      !isEqual(changes.options.previousValue, this.options);

    if (columnsChanged || optionsChanged) {
      this.columnDefinitions = this.composeColumnsDefinitions();
      this.gridApi?.setColumnDefs(this.columnDefinitions);
      this.columnApi?.resetColumnState();
    }
    const noGridHeight = get(this.userGridOptions, 'noGridHeight', false);
    const optionGridHeight$ = get(this.options, 'gridHeightObs');
    const fallbackGridHeight = noGridHeight
      ? null
      : get(this.options, 'gridHeight', DEFAULT_GRID_HEIGHT);

    this.gridHeight$ =
      !noGridHeight && !isNil(optionGridHeight$)
        ? (optionGridHeight$ as Observable<string | number>)
        : asObservable(fallbackGridHeight);

    this.gridMinHeight = <string>(
      get(this.options, 'gridMinHeight', DEFAULT_MINIMUM_GRID_HEIGHT)
    );

    if (optionsChanged) {
      this.inlineAddButtonCaption = get(
        this.options,
        'inlineAddButtonCaption',
        this.staticTextContent.inlineAddButtonCaption,
      ) as string;
    }

    if ('items' in changes) {
      this.rowData = this.items;
      // removed unnecessary row refresh

      const gridAction: XuxGridAction = {
        type: XUX_GRID_ACTIONS.ITEMS_ARRAY_CHANGED,
        payload: { rowData: this.items },
      };

      this.emitAction(gridAction);
    }

    if ('rowButtons' in changes) {
      this.context.buttons.next(this.rowButtons);
    }

    if ('showLoader' in changes) {
      this.gridOptions.suppressNoRowsOverlay = this.showLoader;
      this.customOverlayNoRowsTemplate = this.i18nService.instant(
        this.userGridOptions?.overlayNoRowsTemplate || I18n.Common.NoRowsToShow,
      );

      if (this.showLoader) {
        this.gridApi?.hideOverlay();
      } else {
        this.gridApi?.showNoRowsOverlay();
      }
    }

    this.gridOptions.overlayNoRowsTemplate =
      this.options.overlayNoRowsTemplate ||
      this.gridOptions.overlayNoRowsTemplate;

    if ('pinnedTopRowData' in changes && !isEmpty(this.pinnedTopRowData)) {
      this.pinnedRowPresent = true;
    }
  }

  public isInfiniteRowModel(): boolean {
    return (
      get(this.userGridOptions, 'rowModelType') === GRID_ROW_MODEL_TYPE.INFINITE
    );
  }

  public async emitAction(gridAction: XVIGridAction): Promise<void> {
    const actionWithData = {
      ...gridAction,
      payload: {
        rowData: this.getSelectedItems(),
        ...get(gridAction, 'payload', {}),
      },
    };

    if (
      !(await shouldEmitAction(
        <XuxGridAction>actionWithData,
        this.actionAcceptanceValidators,
      ))
    ) {
      return;
    }

    const actionData = this.handleInternalActions(actionWithData);
    if (actionData) {
      // Emitting action has been wrapped with NgZone.run() because AgGrid listens to some event out of Angulars Zone.
      // An example of such event is row selection handled by .onSelectionChanged().
      // Can be reproduced with XDM_15859 AQA test in DM UI.
      // The issue was faced AgGrid on v22.1.1 (both angular & community packages).
      // eslint-disable-next-line @typescript-eslint/no-unused-expressions
      NgZone.isInAngularZone()
        ? this.action.emit(actionData)
        : this.ngZone.run(() => this.action.emit(actionData));
    }
  }

  public handleInternalActions = (action: XVIGridAction): XVIGridAction => {
    switch (action.type) {
      case XUX_GRID_ACTIONS.LIST_GROUP_COLLAPSE_ALL:
        this.allCollapsed = true;
        this.setExpandState(false);
        break;
      case XUX_GRID_ACTIONS.LIST_GROUP_EXPAND_ALL:
        this.allCollapsed = false;
        this.setExpandState(true);
        break;
      case XUX_GRID_ACTIONS.LIST_GROUP_COLLAPSED:
      case XUX_GRID_ACTIONS.LIST_GROUP_EXPANDED:
        this.allCollapsed = this.calculateAllCollapsed();
        break;
      case XUX_GRID_ACTIONS.QUICK_FILTER_CHANGED:
        if (this.options.enableCustomFiltering) {
          break;
        }
        if (!this.gridOptions.enableServerSideFilter) {
          const searchString = get(action, ['payload', 'value'], '');
          const filterModelComposer: FilterModelComposer = get(
            this,
            'options.filterModelComposer',
          );
          const dataSource: IDatasource = get(
            this,
            'userGridOptions.datasource',
          );
          if (this.isInfiniteRowModel() && filterModelComposer && dataSource) {
            const filterModel = filterModelComposer(searchString);
            this.gridApi.setFilterModel(filterModel);
            this.gridApi.setDatasource(dataSource);

            return action;
          }
          if (this.isTreeViewMode()) {
            this.quickFilterSearchString = searchString;
            this.gridApi.onFilterChanged();
          } else {
            this.gridApi.setQuickFilter(searchString);
          }
        }
        break;
      default:
        break;
    }

    return action;
  };

  public deselectAllGridRows = (): void => {
    if (this.gridOptions.api) {
      this.gridOptions.api.deselectAll();
      this.gridOptions.api.setFocusedCell(-1, '');
    }
  };

  public onRowGroupOpened = (event: RowGroupOpenedEvent): void => {
    const isNodeExpanded: boolean = get(event, 'node.expanded', false);
    const type = isNodeExpanded
      ? XUX_GRID_ACTIONS.LIST_GROUP_EXPANDED
      : XUX_GRID_ACTIONS.LIST_GROUP_COLLAPSED; // eslint-disable-line max-len
    const rowId = get(event, 'node.id', '');
    const rowIndex = get(event, 'node.rowIndex', -1);

    const gridAction: XuxGridAction = {
      type,
      payload: {
        rowId,
        rowIndex,
        gridApi: this.gridApi,
      },
    };
    this.emitAction(gridAction);
  };

  public onBodyScroll = (event: BodyScrollEvent): void => {
    this.context.onBodyScroll.next(event);
  };

  public gridResize(): void {
    this.gridAutoSize();
  }

  public insertNewItem(): void {
    if (this.isCellEditInProgress) return;

    const action: XuxGridAction = {
      type: XUX_GRID_ACTIONS.ADD_ROW,
    };
    this.emitAction(action);
  }

  public getRowData(): null | object[] {
    return this.isInfiniteRowModel() ? null : this.rowData;
  }

  public isRowsAutoheightMode(): boolean {
    return this.columns.some((column) => column.autoHeight);
  }

  protected handleColQuickFilter(
    column: ListColumn | ColDef,
  ): ListColumn | ColDef {
    const quickFilter = get(column, 'filterParams.quickFilter');
    const quickFilterText = get(column, 'getQuickFilterText');

    if (!quickFilter && !quickFilterText) {
      // set return '' to make sure can't be filtered.
      column['getQuickFilterText'] = (_params): string => '';
    }

    return column;
  }

  protected onCellClicked(event: CellClickedEvent): void {
    this.emitAction({
      type: CUSTOM_XUX_GRID_ACTIONS.CELL_CLICKED,
      payload: {
        gridApi: event.api,
        columnApi: event.columnApi,
        rowData: [event.data],
        rowIndex: event.rowIndex,
        rowId: event.node.id,
        field: event.colDef.field,
        columnDef: event.colDef,
      },
    });
  }

  private getEventHandlersForGridOptions(): GridOptions {
    const gridOptions: GridOptions = {
      onCellClicked: this.onCellClicked.bind(this),
      onGridReady: this.onGridReady,
      onRowDoubleClicked: this.rowDoubleClicked,
      onRowClicked: this.rowClicked,
      onSelectionChanged: this.onSelectionChanged,
      onRowSelected: get(
        this.options,
        `gridActionHandlers.${XUX_GRID_ACTIONS.ROW_SELECTED}`,
        this.onRowSelected,
      ),
      onFilterChanged: this.handleGridFilterModelChanges.bind(this),
      onSortChanged: this.emitSortModelChanged,
      onFirstDataRendered: (): void => this.gridResize(),
      onGridSizeChanged: (): void => this.gridResize(),
      onCellEditingStarted: (): boolean => (this.isCellEditInProgress = true),
      onCellEditingStopped: (): boolean => this.onCellEditingStopped(),
      getRowNodeId: this.getRowNodeId,
      onRowDragMove: this.onRowDragMove,
      onRowDragEnd: this.onRowDragEnd,
      onRowGroupOpened: this.onRowGroupOpened,
      onBodyScroll: this.onBodyScroll,
    };

    if (this.isTreeViewMode()) {
      gridOptions.isExternalFilterPresent = (): boolean =>
        !isEmpty(this.quickFilterSearchString);
      gridOptions.doesExternalFilterPass = this.externalFilter.bind(this);
    }

    return gridOptions;
  }

  private externalFilter(node: RowNode): boolean {
    const quickFilterColumns = filter(this.columns, 'filterParams.quickFilter');

    return this.searchByTree(node, quickFilterColumns);
  }

  private searchByTree(
    node: RowNode,
    quickFilterColumns: ListColumn[],
  ): boolean {
    const item = get(node, 'data');
    const isParentMathesQuery = this.searchPredicate(
      this.quickFilterSearchString,
      item,
      quickFilterColumns,
    );
    // if parent item matches a query we have to show it with all subitems
    if (isParentMathesQuery) {
      return true;
    }

    const parentNode: RowNode = get(node, 'parent');
    if (parentNode) {
      const parentItemData = get(parentNode, 'data');

      return this.searchPredicate(
        this.quickFilterSearchString,
        parentItemData,
        quickFilterColumns,
      );
    }

    const treeChildrenPropName = get(
      this.options,
      'treeChildrenPropertyName',
      '',
    );
    const subItems = get(item, treeChildrenPropName, []);
    const filteredSubitems = filter(subItems, (subItem: object): boolean =>
      this.searchPredicate(
        this.quickFilterSearchString,
        subItem,
        quickFilterColumns,
      ),
    );

    return !isEmpty(filteredSubitems);
  }

  private onCellEditingStopped(): boolean {
    /*
      A deferred check of existing of editable cells in a grid.
      If there is no cell in edit mode the 'ITEMS_DATA_CHANGED' action will fire.
    */
    setTimeout(() => {
      if (!this.isCellEditInProgress) {
        const gridAction: XuxGridAction = {
          type: XUX_GRID_ACTIONS.ITEMS_DATA_CHANGED,
          payload: { rowData: this.items },
        };

        this.emitAction(gridAction);
      }
    }, 0);

    /*
      switching this flag to a 'false' state should always be synchronous
      it means that all needed operations on editable cell were completed
    */
    return (this.isCellEditInProgress = false);
  }

  private getNodeChildDetails = (rowItem: any): NodeChildDetails => {
    const treeChildrenPropName = get(
      this.options,
      'treeChildrenPropertyName',
      DEFAULT_TREE_CHILDREN_PROPERTY,
    );

    if (has(rowItem, treeChildrenPropName)) {
      const nodeId = this.getRowNodeId(rowItem);
      const nodes = this.gridApi ? this.gridApi.getRenderedNodes() : [];
      const node = find(nodes, { id: nodeId });

      return {
        expanded: get(node, 'expanded', !this.allCollapsed),
        group: get(rowItem, 'group', true),
        children: rowItem[treeChildrenPropName],
        key: rowItem.entity_id,
      };
    }

    return null;
  };

  private getTreeGridOptions(): Partial<GridOptions> {
    return this.isTreeViewMode()
      ? { getNodeChildDetails: this.getNodeChildDetails }
      : {};
  }

  private getScrollGridOptions(
    gridOptions: Partial<GridOptions>,
  ): Partial<GridOptions> {
    const extraGridOptions = {};
    const deltaRowDataMode: boolean = gridOptions.deltaRowDataMode;
    const orderControls: boolean = this.options.orderControls;
    if (!deltaRowDataMode && orderControls) {
      set(extraGridOptions, 'suppressScrollOnNewData', true);
    }

    return extraGridOptions;
  }

  private setExpandState(value: boolean): void {
    if (!this.gridApi) {
      return;
    }

    if (value) {
      this.gridApi.expandAll();
    } else {
      this.gridApi.collapseAll();
    }

    /*  onGroupExpandedOrCollapsed() should be call to redraw the grid just once.
     *  https://www.ag-grid.com/javascript-grid-grouping/
     */
    this.gridApi.onGroupExpandedOrCollapsed();
  }

  private composeGridOptions(): Partial<GridOptions> {
    const gridOptions = merge(
      {},
      DEFAULT_GRID_OPTIONS,
      this.getTreeGridOptions(),
      this.getEventHandlersForGridOptions(),
      {
        suppressNoRowsOverlay: isNil(this.showLoader) ? false : this.showLoader,
      },
      this.userGridOptions,
      // hide warning message in the console if using a gridOptions or colDef property that doesn't exist.
      { suppressPropertyNamesCheck: true },
    );

    return merge(gridOptions, this.getScrollGridOptions(gridOptions));
  }

  private updateColumnVisibility(): void {
    if (!this.gridApi) {
      return;
    }

    const visibleColumnIds = compact(
      uniq(
        [].concat(
          map(reject(this.columnDefinitions, 'hide'), 'colId'),
          this.view.selectedColumns,
          COLUMN_SELECTOR_ID,
          ROW_SELECTOR_ID,
        ),
      ),
    );

    const hiddenColumns = difference(
      map(this.columns, 'path'),
      visibleColumnIds,
    );

    this.columnApi.setColumnsVisible(visibleColumnIds, true);

    if (!isEmpty(hiddenColumns)) {
      this.columnApi.setColumnsVisible(hiddenColumns, false);
    }

    this.gridResize();
  }

  private updateSearchField(): void {
    const quickFilterColumns = filter(this.columns, 'filterParams.quickFilter');
    // showSearch property from options has precedence over calculated value.
    // If showSearch is not defined, then we'll check for columns affected by quickFilter
    this.showSearch = get(
      this.options,
      'showSearch',
      !isEmpty(quickFilterColumns),
    );
    // compose search placeholder. If its not defined - calculate value using
    // columns affected by a quickFilter
    const quickFilterColumnPart = map(quickFilterColumns, 'title').join(
      ' and ',
    );
    this.searchPlaceholder = get(
      this.options,
      'searchPlaceholder',
      `${this.staticTextContent.search} ${quickFilterColumnPart}`,
    );
  }

  /**
   * ag grid onSelectionChanged handler
   *
   * stores selected items and emits appropiate action
   */
  private onSelectionChanged = (event: SelectionChangedEvent): void => {
    const selectedNodes = event.api.getSelectedNodes();
    const focusedCell = event.api.getFocusedCell();
    const rowIndex = get(focusedCell, 'rowIndex', -1);
    this.selectedItems = map(selectedNodes, 'data');
    this.emitAction({
      type: XUX_GRID_ACTIONS.ROW_SELECTION_CHANGED,
      payload: { rowIndex },
    });
  };

  /**
   * ag grid onRowSelected handler
   *
   * emit action when row is selected/unselected
   * Difference between onSelectionChanged and this method is that onRowSelected
   * emit row that was clicked and not current selected rows
   */
  private onRowSelected = (event: SelectionChangedEvent): void => {
    this.emitAction({
      type: XUX_GRID_ACTIONS.ROW_SELECTED,
      payload: { event },
    });
  };

  private composeColumnsDefinitions(): ColDef[] {
    const columns = map(this.columns, (item: ListColumn) => {
      if (item.marryChildren && !isEmpty(item.children)) {
        forEach(item.children, this.handleColQuickFilter);
      }
      let column = composeColumnDefinition(
        item,
        this.staticTextContent.headerComponentParams,
      );

      column = this.handleColQuickFilter(column);

      return column;
    });

    // Optional supplimentary columns
    // checkboxes column
    if (this.isMultiSelectEnabled()) {
      const userProvidedSelectColumn = find(columns, {
        colId: ROW_SELECTOR_ID,
      });

      if (!isNil(userProvidedSelectColumn)) {
        remove(columns, userProvidedSelectColumn);
      }

      // here we are using spread operator to avoid mutations when infinite row model is enabled
      columns.unshift(
        merge(
          {},
          ROW_SELECTION_COLUMN_DEFINITION,
          userProvidedSelectColumn ?? {},
        ),
      );
    }

    // column selector
    if (!this.options.disableColumnSelector) {
      const selectorColumnDefinition = find(columns, {
        colId: COLUMN_SELECTOR_ID,
      });

      const columnSelectorI18n = this.staticTextContent.columnSelectorI18n;

      // Update the column with passed config if the column with selector's ID exists in options
      if (selectorColumnDefinition) {
        const index = findIndex(columns, { colId: COLUMN_SELECTOR_ID });
        // Remove params that change the header template
        const config = omit(selectorColumnDefinition, [
          'colId',
          'filterParams',
          'headerComponentFramework',
          'filter',
          'suppressFilter',
        ]);
        columns[index] = Object.assign(
          {},
          this.getColumnSelectorDef(columnSelectorI18n),
          config,
        );
      } else {
        columns.push(this.getColumnSelectorDef(columnSelectorI18n));
      }
    }

    if (this.options.showRowOptions) {
      const optionsColumnDefinition = find(columns, {
        colId: COLUMN_SELECTOR_ID,
      });
      set(optionsColumnDefinition, 'cellRenderer', 'rowOptionsRenderer');
      // In case of row options pinned field should be unset.
      // This will make the column unpinned ond fix drop-down menu cutting
      unset(optionsColumnDefinition, 'pinned');
    }

    // drag handle column
    if (this.options.orderControls) {
      const field = get(
        this.options,
        'orderGhostLabelPath',
        DEFAULT_TEXT_FIELD,
      );
      columns.unshift(Object.assign({ field }, ROW_DRAG_COLUMN_DEFINITION));
    }

    // remove row column
    if (this.options.inlineRemove) {
      const inlineRemoveOptions = get(this, 'options.inlineRemoveParams', null);

      columns.push({
        ...ROW_REMOVE_COLUMN_DEFINITION,
        cellRendererParams: inlineRemoveOptions,
      });
    }

    // set cellRenderer 'agGroupCellRenderer' in column for grid tree mode
    if (this.isTreeViewMode()) {
      const specificTreeColumnTitle = get(
        this.userGridOptions,
        'treeColumnPath',
        DEFAULT_TREE_COL_ID,
      );
      const colId = get(
        this.options,
        'treeColumnTitle',
        specificTreeColumnTitle,
      ) as string;
      const treeColumn = find(columns, { colId });
      if (treeColumn) {
        set(treeColumn as any, 'cellRenderer', 'agGroupCellRenderer');
      }
    }

    // if infinite row model is enabled we are replacing selectAll checkbox header with custom
    // solution as native selectAll is not supported with infinite row model enabled as per docs
    if (this.isInfiniteRowModel() && this.isMultiSelectEnabled()) {
      const rowSelectorColumn = find(columns, { colId: ROW_SELECTOR_ID });

      Object.assign(rowSelectorColumn, {
        headerComponentFramework: CustomSelectAllComponent,
        headerCheckboxSelection: false,
        headerComponentParams: {
          dataSource: get(this, 'userGridOptions.datasource'),
        },
      });
    }

    return columns;
  }

  private getColumnSelectorDef(i18n: ComponentStaticText): ColDef {
    const columnSelectorDef = this.composeColumnSelector(this.columns);
    if (Array.isArray(columnSelectorDef.filterParams.values)) {
      this.columnSelectorValues$.next(columnSelectorDef.filterParams.values);
      columnSelectorDef.filterParams.values =
        this.columnSelectorValues$.asObservable();
    }

    return {
      ...columnSelectorDef,
      filterParams: {
        ...(columnSelectorDef.filterParams || {}),
        i18n,
      },
    };
  }

  private composeColumnSelector(columnConfig: ListColumn[]): ColDef {
    // default column should be valued to selectedColumns
    set(
      this.view,
      'selectedColumns',
      map(
        filter(columnConfig, (listColumn: ListColumn) => {
          return (listColumn as IXVIListColumn).selected;
        }),
        'path',
      ),
    );
    this.setFilterModel();
    const data = filter(columnConfig, (listColumn: ListColumn): boolean => {
      return (listColumn as IXVIListColumn).canSelected;
    });

    return this.mergeReplaceArrays({}, OPTIONS_COLUMN_DEFINITION, {
      filterParams: {
        values: map(data, (column: ListColumn) => ({
          text: column.title || startCase(column.path),
          value: column.path,
        })),
        default: {
          filter: uniq([
            ...map(filter(columnConfig, 'selected'), 'path'),
            'name',
          ]),
        },
      },
    });
  }

  /**
   * mergeReplaceArrays function should work same way as lodash merge except it completely
   *
   * replaces arrays with new ones
   *
   * @param result - object to be merged in
   * @param sources - other sourceObject to be merged
   */

  private mergeReplaceArrays(result: object, ...sources: object[]): object {
    return mergeWith(result, ...sources, (value: any, srcValue: any) =>
      isArray(srcValue) || isArray(value) ? srcValue : undefined,
    );
  }

  private isValidIndex(index: number): boolean {
    return isNumber(index) && index >= 0;
  }

  private gridAutoSize = (): void => {
    if (this.isGridVisible() && !this.userGridOptions.suppressAutoSize) {
      this.gridApi.sizeColumnsToFit();
    }
  };

  private isGridVisible(): boolean {
    return !!get(this.gridApi, 'gridCore.eGridDiv.clientWidth');
  }

  private handleGridFilterModelChanges = (): void => {
    const rawFilterModel = this.gridApi.getFilterModel();
    const selectedColumnIds = get(
      rawFilterModel,
      [COLUMN_SELECTOR_ID, 'filter'],
      [],
    );
    const selectedColumns = get(this.view, 'selectedColumns');
    if (
      !isEqual(compact(selectedColumns), compact(selectedColumnIds)) &&
      !this.skipColumnSelectionChangeEvent
    ) {
      this.emitAction({
        type: XUX_GRID_ACTIONS.COLUMN_SELECTION_CHANGED,
        payload: {
          selectedColumns: selectedColumnIds,
        },
      });
    }

    const filterModel: FilterModel = omit(rawFilterModel, COLUMN_SELECTOR_ID);

    if (isEqual(this.lastEmittedFilterModel, filterModel)) {
      return;
    }

    // calculating new model, which is complemented with default filters for visible columns
    // In case if its differs from current filter model - set new model and exit from this function.
    // We'll get here again with updated model via onFilterChanged event handler.
    const modelWithDefaults = {
      ...this.getDefaultFilters(),
      ...filterModel,
    };

    if (!isEqual(modelWithDefaults, filterModel)) {
      this.setFilterModel();

      return;
    }

    this.lastEmittedFilterModel = cloneDeep(filterModel);

    const gridAction: XuxGridAction = {
      type: XUX_GRID_ACTIONS.FILTER_MODEL_CHANGED,
      payload: {
        filterModel,
        sortModel: this.gridApi.getSortModel(),
      },
    };
    this.emitAction(gridAction);
  };

  private emitSortModelChanged = (): void => {
    const sortModel = this.gridApi.getSortModel();
    if (isEqual(this.lastEmittedSortModel, sortModel)) {
      return;
    }

    this.lastEmittedSortModel = sortModel;

    const gridAction: XuxGridAction = {
      type: XUX_GRID_ACTIONS.SORT_MODEL_CHANGED,
      payload: {
        sortModel,
        filterModel: omit(this.gridApi.getFilterModel(), COLUMN_SELECTOR_ID),
      },
    };
    this.emitAction(gridAction);
  };

  private getSelectedItems(): object[] {
    if (!this.gridApi) {
      return [];
    }
    const selectedNodes = this.gridApi.getSelectedNodes();

    return (this.selectedItems = map(selectedNodes, 'data'));
  }

  /**
   * addDefaultFilters
   *
   * appends filterModel with filters for visible column in case
   * if column is visible,
   */
  private getDefaultFilters(): FilterModel {
    const selectedColumnIds = <string[]>get(this.view, 'selectedColumns', []);

    const columnsToAddFilter = this.columns.filter(
      (column) =>
        has(column, 'filterParams.default') &&
        !has(this.view.filterModel, column.path) &&
        selectedColumnIds.includes(column.path),
    );

    return Object.assign(
      {},
      ...columnsToAddFilter.map((column) => ({
        [column.path]: column.filterParams.default,
      })),
    );
  }

  private rowDoubleClicked = (event: RowDoubleClickedEvent): void => {
    // check if current cell has editor - do nothing
    if (this.isCellEditInProgress) {
      return;
    }
    this.emitAction({
      type: XUX_GRID_ACTIONS.ROW_DOUBLE_CLICKED,
      payload: {
        rowData: [event.data],
        rowIndex: event.rowIndex,
      },
    });
  };

  private rowClicked = (event: RowClickedEvent): void => {
    // check if current cell has editor - do nothing
    if (this.isCellEditInProgress) {
      return;
    }
    this.emitAction({
      type: XUX_GRID_ACTIONS.ROW_CLICKED,
      payload: {
        rowData: [event.data],
        rowIndex: event.rowIndex,
      },
    });
  };

  private setFilterModel(): void {
    if (this.gridApi) {
      const filterModel = {
        ...this.getDefaultFilters(),
        ...this.view.filterModel,
      };

      if (!this.options.disableColumnSelector) {
        set(filterModel, COLUMN_SELECTOR_ID, {
          type: 'equals',
          filter: this.view.selectedColumns || [],
        });
      }
      this.gridApi.setFilterModel(filterModel);
    }
  }

  private onRowDragMove = (rowEvent: RowDragEvent): void => {
    const movingNode = rowEvent.node;
    const overNode = rowEvent.overNode;

    const parentMovingNode = get(movingNode, 'parent', null);
    const parentOverNode = get(overNode, 'parent', null);
    const groupMovingNodeIndex = this.rowData.indexOf(
      get(parentMovingNode, 'data'),
    );
    const groupOverNodeIndex = this.rowData.indexOf(
      get(parentOverNode, 'data'),
    );
    const isTreeChildrenItem =
      this.isValidIndex(groupMovingNodeIndex) &&
      this.isValidIndex(groupOverNodeIndex);
    if (isTreeChildrenItem) {
      const newRowDataWithChild = this.rowData.concat();
      const childrenPropertyName = get(
        this.options,
        'treeChildrenPropertyName',
        DEFAULT_TREE_CHILDREN_PROPERTY,
      );
      const group = newRowDataWithChild[groupMovingNodeIndex];
      const childrenItems = get(group, [childrenPropertyName], []);
      const movingNodeIndex = childrenItems.indexOf(get(movingNode, 'data'));
      const overNodeIndex = childrenItems.indexOf(get(overNode, 'data'));
      if (
        movingNodeIndex !== overNodeIndex &&
        this.isValidIndex(overNodeIndex)
      ) {
        [childrenItems[movingNodeIndex], childrenItems[overNodeIndex]] = [
          childrenItems[overNodeIndex],
          childrenItems[movingNodeIndex],
        ];

        this.rowData = newRowDataWithChild;
      }
    }
    if (movingNode !== overNode && !isTreeChildrenItem) {
      const fromIndex = this.rowData.indexOf(get(movingNode, 'data'));
      const toIndex = this.rowData.indexOf(get(overNode, 'data'));
      if (!this.isValidIndex(fromIndex) || !this.isValidIndex(toIndex)) {
        return;
      }
      const newRowData = this.rowData.concat();
      // reorder items array
      [newRowData[fromIndex], newRowData[toIndex]] = [
        newRowData[toIndex],
        newRowData[fromIndex],
      ];

      this.rowData = newRowData;

      if (!get(this.gridOptions, 'deltaRowDataMode')) {
        this.gridApi.clearFocusedCell();
      }
    }
  };

  private onRowDragEnd = (rowEvent: RowDragEvent): void => {
    const gridAction: XuxGridAction = {
      type: XUX_GRID_ACTIONS.ITEM_ORDER_CHANGED,
      payload: {
        rowData: this.rowData,
        value: rowEvent,
      },
    };
    this.emitAction(gridAction);
  };

  private getRowNodeId = (data: any): string => {
    const valueField = get(this, 'options.valueField', DEFAULT_VALUE_FIELD);

    return get(data, valueField);
  };

  private onGridReady = (params: GridReadyEvent): void => {
    this.gridApi = params.api;
    this.columnApi = params.columnApi;
    this.updateColumnVisibility();

    const context = get(this.gridApi, [
      'gridOptionsWrapper',
      'gridOptions',
      'context',
    ]);

    if (context) {
      // update context
      set(context, 'isTreeViewMode', this.isTreeViewMode());
    }

    // setRowData cannot be used with infinite row model as per docs
    if (!this.isInfiniteRowModel() && !this.isTreeViewMode()) {
      params.api.setRowData(this.rowData);
    }
    params.api.resetRowHeights();

    this.emitAction({
      type: XUX_GRID_ACTIONS.GRID_READY,
      payload: {
        gridApi: this.gridApi,
      },
    });
  };

  private calculateAllCollapsed = (): boolean => {
    let allCollapsed = true;
    this.gridOptions.api.forEachNode((node: RowNode) => {
      if (node.expanded) {
        allCollapsed = false;
      }
    });

    return allCollapsed;
  };

  private getInitialRowHeight(): GridRowHeightName {
    return get(this.options, 'gridRowHeight', GridRowHeightName.MEDIUM);
  }

  private searchPredicate(
    searchString: string,
    item: any,
    quickFilterColumns: ListColumn[],
  ): boolean {
    return some(
      quickFilterColumns,
      (quickFilterColumn: ListColumn): boolean => {
        const defaultRowText: string = get(item, quickFilterColumn.path);
        const getQuickFilterRowText: GetQuickFilterTextForTreeViewType = get(
          quickFilterColumn,
          'getQuickFilterTextForTreeView',
        );
        const rowText = getQuickFilterRowText
          ? getQuickFilterRowText(item)
          : defaultRowText;

        return includes(toLower(rowText), toLower(searchString));
      },
    );
  }

  private isMultiSelectEnabled(): boolean {
    return get(this, 'options.multiselect', false);
  }

  private isTreeViewMode(): boolean {
    return get(this, 'options.treeViewMode', false);
  }
}
